import type { AdminQuoteRow, QuoteRequestStatus } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import {
  activeQuoteWarnings,
  GUEST_CONTACT_METHOD_LABELS,
  QUOTE_STATUS_LABELS,
} from '@/entities/quote';
import { formatCount, formatDateTime, formatWon } from '@/shared/lib';
import {
  Button,
  cn,
  DesignThumbnail,
  StatusBadge,
  type StatusTone,
  tableStyles,
} from '@/shared/ui';

export interface QuoteRowProps {
  /** 계약 타입을 그대로 받는다. */
  readonly quote: AdminQuoteRow;
  readonly onOpen: (quoteRequestId: string) => void;
}

/**
 * 견적 상태 배지 톤 — 주문 목록과 같은 기준으로, 운영자가 지금 무엇을 해야 하는지로 나눈다.
 * attention 새 요청(검토 대기) / warning 고객이 수정을 요청한 건 / neutral 발행 뒤 고객 답을 기다리는 건 /
 * positive 주문으로 이어진 건 / danger 더 진행되지 않는 건(거절·반려·취소·만료).
 */
const STATUS_TONES: Readonly<Record<QuoteRequestStatus, StatusTone>> = {
  REQUESTED: 'attention',
  REVISION_REQUESTED: 'warning',
  ISSUED: 'neutral',
  ORDERED: 'positive',
  DECLINED: 'danger',
  REJECTED: 'danger',
  CANCELED: 'danger',
  EXPIRED: 'danger',
};

const MUTED_CLASS = 'text-muted-foreground';
const SUB_TEXT_CLASS = 'text-xs text-muted-foreground';

/**
 * 견적 목록의 한 행(견적 설계 §6-1) — 상품·수량 칸 왼쪽에 견적 썸네일(상품 목록과 같은 44px, 디자인 없는 일반 견적은 상품 대표 사진), 경고(상품 숨김·삭제, 색상 공급 불가)는 배지로.
 * 비회원이 접수한 견적은 회사·담당자 아래에 '비회원' 배지와 연락 방법을, 회원 계정에 연결됐으면 '연결됨'을 붙인다(비회원 견적 설계 G14).
 */
export const QuoteRow = ({ quote, onOpen }: QuoteRowProps): ReactElement => {
  const status = QUOTE_STATUS_LABELS[quote.status];
  const warnings = activeQuoteWarnings(quote.warnings);

  return (
    <tr className={tableStyles.row}>
      <td className={cn(tableStyles.td, 'font-medium whitespace-nowrap')}>{quote.quoteNumber}</td>
      <td className={cn(tableStyles.td, 'whitespace-nowrap')}>{formatDateTime(quote.createdAt)}</td>
      <td className={cn(tableStyles.td, 'min-w-32')}>
        {quote.companyName === null
          ? quote.contactName
          : `${quote.companyName} · ${quote.contactName}`}
        {quote.guest === null ? null : (
          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            <StatusBadge tone="warning">비회원</StatusBadge>
            <span className={SUB_TEXT_CLASS}>
              {GUEST_CONTACT_METHOD_LABELS[quote.guest.contactMethod]}
              {quote.guest.claimed ? ' · 연결됨' : ''}
            </span>
          </span>
        )}
      </td>
      <td className={cn(tableStyles.td, 'min-w-48')}>
        <div className="flex items-center gap-3">
          <DesignThumbnail
            url={quote.thumbnailUrl ?? quote.productImageUrl}
            alt={`${quote.quoteNumber} 디자인 미리보기`}
            className="size-11 shrink-0 rounded-lg bg-muted"
          />
          <span className="break-keep">
            {quote.productName} · {formatCount(quote.quantity)}개
          </span>
        </div>
      </td>
      <td className={cn(tableStyles.td, 'whitespace-nowrap tabular-nums')}>
        {quote.desiredDate ?? <span className={MUTED_CLASS}>-</span>}
      </td>
      <td className={tableStyles.td}>
        <StatusBadge tone={STATUS_TONES[quote.status]}>{status.label}</StatusBadge>
      </td>
      <td className={cn(tableStyles.td, 'text-right whitespace-nowrap tabular-nums')}>
        {quote.latestIssuedTotalAmount === null ? (
          <span className={MUTED_CLASS}>-</span>
        ) : (
          formatWon(quote.latestIssuedTotalAmount)
        )}
      </td>
      <td className={tableStyles.td}>
        {warnings.length === 0 ? (
          <span className={MUTED_CLASS}>-</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {warnings.map((warning) => (
              <StatusBadge key={warning} tone="danger">
                {warning}
              </StatusBadge>
            ))}
          </span>
        )}
      </td>
      <td className={cn(tableStyles.td, 'text-right')}>
        <Button
          variant="outline"
          size="sm"
          aria-label={`${quote.quoteNumber} 상세`}
          onClick={() => onOpen(quote.id)}
        >
          상세
        </Button>
      </td>
    </tr>
  );
};
